'use client';

import { useState } from 'react';
import { addLine } from '@/lib/cart-store';
import { openCartDrawer } from '@/lib/cart-drawer';
import { trackAddToCart } from '@/lib/analytics';
import { INSTAGRAM_DM_URL } from '@/lib/policies';
import { useSelectedVariant, type PdpVariant } from '@/lib/variant-store';

interface Props {
  productId:   string;
  slug:        string;
  productName: string;
  brand?:      string;
  image?:      string;
  category?:   string;
  variants:    PdpVariant[];
  /** Compact pill: used in the sticky bottom bar on mobile. */
  compact?:    boolean;
}

export function AddToCart({ productId, slug, productName, brand, image, category, variants, compact }: Props) {
  const [status, setStatus] = useState<'idle' | 'added' | 'max'>('idle');
  const resolved = useSelectedVariant(variants);

  const anyBuyable = variants.some((v) => v.canBuy);
  const needsSelection = resolved.hasVariants && !resolved.isComplete;
  // Sold out = nothing on this product can be bought, or the chosen variant can't be.
  const soldOut = !anyBuyable || (resolved.isComplete && !resolved.canBuy);

  function handleAdd() {
    if (needsSelection) {
      const picker = document.getElementById('np-variant-picker');
      if (picker) {
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        picker.classList.add('ring-2', 'ring-gold');
        setTimeout(() => picker.classList.remove('ring-2', 'ring-gold'), 1200);
      }
      return;
    }
    if (status === 'added') {
      openCartDrawer();
      return;
    }
    const v = resolved.variant;
    if (!v || !v.canBuy) return;
    const res = addLine(
      {
        variantId:    v.id,
        productId,
        slug,
        name:         productName,
        variantTitle: resolved.hasVariants ? v.title : null,
        image,
        priceCents:   v.priceCents,
        maxQty:       Math.max(1, v.maxQty),
        inStockQty:   v.inStock ? v.available : 0,
        preorder:     !!v.preorder,
        shipsBy:      v.preorder?.shipsBy ?? null,
        brand,
      },
      1,
    );
    if (!res.ok) {
      setStatus('max');
      setTimeout(() => setStatus('idle'), 3000);
      openCartDrawer();
      return;
    }
    setStatus('added');
    openCartDrawer();
    trackAddToCart({
      item_id:       productId,
      item_name:     productName,
      item_brand:    brand,
      item_category: category,
      item_variant:  resolved.hasVariants ? v.title : undefined,
      price:         v.priceCents / 100,
      quantity:      1,
    });
  }

  const className = compact
    ? 'bg-plum text-white rounded-sm px-5 py-2.5 text-xs uppercase tracking-[0.12em] font-medium hover:bg-plum-deep transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap'
    : 'w-full bg-plum text-white py-4 text-xs uppercase tracking-[0.12em] font-medium hover:bg-plum-deep transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

  const verb = resolved.isPreorder ? 'Pre-order' : 'Add to cart';
  const label = soldOut
    ? 'Sold out'
    : needsSelection
    ? compact ? 'Select size' : 'Select a size'
    : status === 'added'
    ? compact ? 'View cart' : 'Added. View cart'
    : status === 'max'
    ? 'Max in cart'
    : compact ? (resolved.isPreorder ? 'Pre-order' : 'Add') : verb;

  return (
    <div>
      <button
        type="button"
        onClick={handleAdd}
        disabled={soldOut}
        aria-label={soldOut ? `${productName} is sold out` : undefined}
        className={className}
      >
        {label}
      </button>
      {soldOut && !compact && (
        <a
          href={INSTAGRAM_DM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center mt-3 text-xs uppercase tracking-[0.12em] text-ink/70 hover:text-ink underline underline-offset-4 transition-colors"
        >
          Ask about a restock
        </a>
      )}
    </div>
  );
}
